import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Editor, Toolbar } from 'ngx-editor';
import { PageService } from '@core/services/page.service';
import { FileService } from '@core/services/file.service';
import { SectionService } from '@core/services/section.service';
import { SponsorService } from '@core/services/sponsor.service';
import { take } from 'rxjs';
import { IMenuItem } from '@core/interfaces/imenu-item';
import { IPage, IPageAddRequest } from '@core/interfaces/ipage';
import { toHTML } from 'ngx-editor';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmCancelComponent } from 'src/app/component/shared/confirm-cancel/confirm-cancel.component';
import { IModalData } from '@core/interfaces/imodal-data';
import {
  IProfileBio,
  IProfileBioAddRequest,
} from '@core/interfaces/i-profile-bio';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ISponsor, ISponsorAddRequest } from '@core/interfaces/i-sponsor';
import { IPdfFile, IPdfFileAddRequest } from '@core/interfaces/i-file';
import { IFormInput } from '@core/interfaces/iform-input';
import { ModalFormComponent } from 'src/app/component/shared/modal-form/modal-form.component';
import { ProfileBioService } from '@core/services/profile-bio.service';
import { ISectionAddRequest } from '@core/interfaces/isections';
import { SectionType } from '@core/enums/section-types';
import { IDragDropConfiguration } from '@core/interfaces/idrag-drop-configuration';
import { EMAIL_REGEX } from '@core/enums/patterns';
import { MenuItemService } from '@core/services/menu-item.service';
import { PageType } from '@core/enums/page-validator';
import { faExclamationTriangle, faUpload } from '@fortawesome/free-solid-svg-icons';

enum AddType {
  ProfileBio = 'Profile Bio',
  Sponsor = 'sponsor',
  Files = 'files',
}
@Component({
    selector: 'app-page-editor',
    templateUrl: './page-editor.component.html',
    styleUrls: ['./page-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PageEditorComponent implements OnInit, OnDestroy {
  constructor(
    private page: PageService,
    private link: MenuItemService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private modal: NgbModal,
    private sponsor: SponsorService,
    private files: FileService,
    private profileBio: ProfileBioService,
    private section: SectionService
  ) { }

  configuration: IDragDropConfiguration = {
    imageClass: 'small',
    imageTagClass: 'profile-img',
    editable: false,
  };

  SectionType = SectionType;
  editor!: Editor;
  editablePages!: IMenuItem[];
  pagesArr: string[] = [];
  containerIds: string[] = [];
  AddType = AddType;
  profileBioErrorMessage: string = '';

  internalForm: FormGroup | any;
  externalForm: FormGroup | any;
  showInternalContent: boolean = true;
  showExternalContent: boolean = false;
  radioOptions = ['internal', 'external'];

  faExclamationTriangle = faExclamationTriangle;
  faUpload = faUpload;

  @ViewChild('image') image!: FormControl;

  toolbar: Toolbar = [
    ['bold', 'italic', 'underline'],
    // ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    // @todo: Racca - modify toolbar settings
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['link'],
    // ['link', 'image'],
    // ['text_color', 'background_color'],
    // ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  form!: FormGroup | any;
  pageContent!: IPage | null;
  imageFile!: File;
  fileNameDisplay!: string;
  pageMenuItemId!: number;
  isNew: boolean = false;
  subMenuItems: IMenuItem[] = [];

  addSectionForm: IFormInput[] = [
    {
      controlName: 'name',
      label: 'Name',
      type: 'text',
      validators: [Validators.required, Validators.maxLength(250)],
      required: true,
      maxLength: 250,
      helper: '(Max length 250 characters)',
    },
    {
      controlName: 'pageId',
      label: 'Page Id',
      type: 'number',
      validators: [Validators.required],
      required: true,
      hidden: true,
      readonly: true,
    },
    {
      controlName: 'sectionTypeId',
      label: 'Section Type',
      type: 'select',
      validators: [Validators.required],
      required: true,
      options: [
        { value: 1, label: 'Profile Bio' },
        { value: 2, label: 'Sponsor' },
        { value: 3, label: 'File' },
      ],
    },
  ];

  addProfileBioForm: IFormInput[] = [
    {
      controlName: 'name',
      label: 'Name',
      type: 'text',
      validators: [Validators.required, Validators.maxLength(250)],
      required: true,
      maxLength: 250,
      helper: '(Max length 250 characters)',
    },
    {
      controlName: 'position',
      label: 'Position',
      type: 'text',
      validators: [Validators.maxLength(100)],
      maxLength: 100,
      helper: '(Max length 100 characters)',
    },
    {
      controlName: 'email',
      label: 'Email',
      type: 'text',
      validators: [
        Validators.maxLength(100),
        Validators.pattern(EMAIL_REGEX)
      ],
      maxLength: 100,
      helper: '(Max length 100 characters)',
      errorMessage: 'Invalid email format. Please enter a valid email address.',
    },
    {
      controlName: 'businessOrgName',
      label: 'Business/Organization Name',
      type: 'text',
      validators: [Validators.maxLength(100)],
      maxLength: 100,
      helper: '(Max length 100 characters)',
    },
    {
      controlName: 'location',
      label: 'Location',
      type: 'text',
      validators: [Validators.maxLength(100)],
      helper: '(Max length 100 characters)',
    },
    {
      controlName: 'image',
      label: 'Profile Image',
      type: 'file',
      buttonOverride: 'button',
      validators: [Validators.required],
      required: true,
      helper: '(.png or .jpeg/.jpg files only)',
      accept: '.png, .jpg, .jpeg',
    },
  ];

  addSponsorForm: IFormInput[] = [
    {
      controlName: 'name',
      label: 'Name',
      type: 'text',
      validators: [Validators.required, Validators.maxLength(250)],
      required: true,
      maxLength: 250,
      helper: '(Max length 250 characters)',
    },
    {
      controlName: 'imageHrefUrl',
      label: 'Sponsor Url',
      type: 'url',
      validators: [Validators.required],
      required: true,
      helper: '(link to open on logo click)',
    },
    {
      controlName: 'image',
      label: 'Logo File',
      type: 'file',
      buttonOverride: 'button',
      validators: [Validators.required],
      required: true,
      helper: '(.png or .jpeg/.jpg files only)',
      accept: '.png, .jpg, .jpeg',
    },
  ];

  addFileForm: IFormInput[] = [
    {
      controlName: 'fileDisplayName',
      label: 'File Display Name',
      type: 'text',
      validators: [Validators.required, Validators.maxLength(250)],
      required: true,
      maxLength: 250,
      helper: '(Max length 250 characters)',
    },
    {
      controlName: 'file',
      label: 'File',
      type: 'file',
      buttonOverride: 'button',
      validators: [Validators.required],
      required: true,
      helper: '(.pdf files only)',
      accept: '.pdf',
    },
  ];

  get editorContent() {
    return this.form.get('editorContent');
  }
  get pageSelection() {
    return this.form.get('pageSelection');
  }
  get pageTitle() {
    return this.form.get('pageTitle');
  }
  get fileUpload() {
    return this.form.get('fileUpload');
  }
  get fileName() {
    return this.form.get('fileName');
  }
  get routerLink() {
    return this.form.get('routerLink');
  }
  get hrefLink() {
    return this.form.get('hrefLink');
  }
  get pageType() {
    return this.form.get('pageType');
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params: ParamMap) => {
      this.pageMenuItemId = Number(params.get('id'));
      if (this.pageMenuItemId == 0) {
        this.isNew = true;
        return;
      }

      this.setContent();
    });

    this.editor = new Editor();

    const urlRegex =
      /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/;

    // Initialize internalForm
    this.internalForm = this.fb.group({
      editorContent: [null, [Validators.required]],
      pageSelection: [0, [Validators.required]],
      pageTitle: [null, [Validators.required, Validators.maxLength(250)]],
      fileUpload: [null],
      fileName: [null],
      routerLink: [
        null,
        [Validators.pattern('^/[a-z-]+$'), Validators.maxLength(50)],
      ],
    });

    // Initialize externalForm
    this.externalForm = this.fb.group({
      pageTitle: [null, [Validators.required, Validators.maxLength(50)]],
      hrefLink: [
        null,
        [
          Validators.required,
          Validators.maxLength(250),
          Validators.pattern(urlRegex),
        ],
      ],
    });

    this.form = this.fb.group({
      pageType: [PageType.Internal],
      pageTitle: [null, [Validators.required, Validators.maxLength(50)]],
      editorContent: [null, [Validators.required]],
      fileUpload: [null],
      fileName: [null],
      routerLink: [null, [Validators.pattern('^/[a-z-]+$'), Validators.maxLength(50)]],
      hrefLink: [
        null,
        [
          Validators.required,
          Validators.maxLength(250),
          Validators.pattern(urlRegex),
        ],
      ],
      internalForm: this.internalForm,
      externalForm: this.externalForm,
    });

    this.form.get('pageType')?.valueChanges.subscribe((value: string) => {
      this.handlePageTypeChange(value);
    });
  }

  handlePageTypeChange(value: string): void {
    if (value === PageType.Internal) {
      this.form.removeControl('hrefLink');
      this.form.addControl('internalForm', this.internalForm);
      this.form.removeControl('externalForm');
    } else if (value === PageType.External) {
      this.form.removeControl('internalForm');
      this.form.addControl('externalForm', this.externalForm);
      this.form.removeControl('routerLink');
      this.form.removeControl('fileUpload');
    }
  }

  isFormValid(): boolean {
    const commonFieldsValid = this.form.get('pageTitle').valid;

    if (this.form.value.pageType === PageType.Internal) {
      const internalForm = this.form.get('internalForm');
      return commonFieldsValid && internalForm?.get('routerLink').valid && internalForm?.get('editorContent').valid;
    } else if (this.form.value.pageType === PageType.External) {
      const externalForm = this.form.get('externalForm');
      return commonFieldsValid && externalForm?.get('hrefLink').valid;
    }

    return false;
  }

  setContent(): void {
    if (this.pageMenuItemId !== 0) {
      this.page.getPageByMenuItemId(this.pageMenuItemId)
        .pipe(take(1))
        .subscribe((res: IPage | IMenuItem | null) => {
          if (res && 'title' in res && 'body' in res) {
            this.showExternalContent = false;
            this.disableExternalLinkForm();
          } else {
            this.showExternalContent = true;
            this.enableExternalLinkForm();
          }

          if (!res) {
            this.pageContent = null;
            this.isNew = this.form.pristine;
            this.form.reset();
            return;
          }

          if ('title' in res && 'body' in res) {
            const internalRes = res as IPage;
            this.pageContent = {
              ...this.pageContent,
              ...internalRes,
              title: internalRes.title,
            };
            this.toggleForm(PageType.Internal); // Initialize the internal form
            this.form.patchValue({
              pageTitle: internalRes.title,
              editorContent: internalRes.body,
              fileName: internalRes.imageFileName,
              routerLink: internalRes.routerLink,
            });
          } else {
            const externalRes = res as IMenuItem;
            this.pageContent = {
              ...this.pageContent,
              ...externalRes,
              title: externalRes.name,
              body: externalRes.hrefLink,
            };
            this.toggleForm(PageType.External); // Initialize the external form
            this.form.patchValue({
              pageTitle: externalRes.name,
              hrefLink: externalRes.hrefLink,
            });
          }

          this.form.markAsPristine();
        });
    }
  }

  disableExternalLinkForm() {
    const externalForm = this.form.get('externalForm');
    if (externalForm) {
      externalForm.disable();
    }
  }

  enableExternalLinkForm() {
    const externalForm = this.form.get('externalForm');
    if (externalForm) {
      externalForm.enable();
    }
  }

  addNewModal: IModalData = {
    title: 'Add New',
    cancelBtnText: 'Cancel & Discard',
    confirmBtnText: 'Save Profile/Sponsor',
    formInputs: this.addSectionForm,
  };

  addSection(): void {
    let fileToUpload!: File;
    let payload!: Partial<ISectionAddRequest>;
    let modalData!: IModalData;
    let modalRef!: NgbModalRef;
    let finalPayload: ISectionAddRequest;

    payload = {
      pageId: this.pageContent!.id,
    };

    modalData = {
      title: 'Add New Section',
      cancelBtnText: 'Cancel & Discard',
      confirmBtnText: 'Add Section',
      formInputs: this.addSectionForm,
    };

    modalData.data = { pageId: this.pageContent!.id };

    modalRef = this.modal.open(ModalFormComponent, { centered: true });
    modalRef.componentInstance.data = modalData;
    modalRef.result
      .then((res: any) => {
        finalPayload = { ...res, ...payload };

        this.section
          .add(finalPayload, 'Section Added')
          .pipe(take(1))
          .subscribe((res: any) => {
            this.discardOrSave();
          });
      })
      .catch((_error: any) => false);
  }

  addNewOrSave(addType: string, sectionId: number, event?: any): void {
    let fileToUpload!: File;
    let payload!: Partial<ISponsorAddRequest | IProfileBioAddRequest | IPdfFileAddRequest>;
    let modalData!: IModalData;
    let modalRef!: NgbModalRef;
    let finalPayload: | ISponsorAddRequest | IProfileBioAddRequest | IPdfFileAddRequest;

    switch (addType) {
      case AddType.ProfileBio:
        this.configuration.editable = true;
        payload = { sectionId: sectionId };
        modalData = {
          title: `${event ? 'Edit' : 'Add New'} Profile Bio`,
          cancelBtnText: 'Cancel & Discard',
          confirmBtnText: 'Save Profile Bio',
          formInputs: this.addProfileBioForm,
        };
        if (event) {
          modalData.data = event;
          payload.id = event.id;
          modalData.fileKey = 'image';
          const eventId = event.id;
        }

        modalRef = this.modal.open(ModalFormComponent, { centered: true });
        modalRef.componentInstance.data = modalData;
        modalRef.componentInstance.formChange.subscribe((file: File) => {
          fileToUpload = file;
          if ('imageFileName' in payload && typeof payload.imageFileName === 'string') {
            payload.imageFileName = file.name;
          }
        });
        modalRef.result
          .then((res: any) => {
            finalPayload = { ...res, ...payload };

            var formData = new FormData();
            formData.append('file', fileToUpload);
            formData.append('profileBio', JSON.stringify(finalPayload));

            this.profileBio
              .add(formData, `Profile bio has been ${event ? 'updated' : 'added'}.`)
              .pipe(take(1))
              .subscribe((res) => {
                this.discardOrSave();
              });

            this.configuration.editable = false;
          })
          .catch((_error: any) => false);
        break;
      case AddType.Sponsor:
        payload = { sectionId: sectionId };
        modalData = {
          title: 'Add New Sponsor',
          cancelBtnText: 'Cancel & Discard',
          confirmBtnText: 'Save Sponsor',
          formInputs: this.addSponsorForm,
          fileKey: 'image',
          data: event
        };

        modalRef = this.modal.open(ModalFormComponent, { centered: true });
        modalRef.componentInstance.data = modalData;
        modalRef.componentInstance.formChange.subscribe((file: File) => {
          fileToUpload = file;
          if (
            'imageFileName' in payload &&
            typeof payload.imageFileName === 'string'
          ) {
            payload.imageFileName = file.name;
          }
        });
        modalRef.result
          .then((res: any) => {
            finalPayload = { ...res, ...payload };

            var formData = new FormData();
            formData.append('file', fileToUpload);
            formData.append('sponsor', JSON.stringify(finalPayload));
            this.sponsor
              .add(formData, 'Sponsor Added')
              .pipe(take(1))
              .subscribe((_res) => this.discardOrSave());
          })
          .catch((_err) => false);
        break;
      case AddType.Files:
        payload = { sectionId: sectionId };
        modalData = {
          title: 'Add New File',
          cancelBtnText: 'Cancel & Discard',
          confirmBtnText: 'Save File',
          formInputs: this.addFileForm,
          fileKey: 'file',
          data: event
        };
        modalRef = this.modal.open(ModalFormComponent, { centered: true });
        modalRef.componentInstance.data = modalData;
        modalRef.componentInstance.formChange.subscribe((file: File) => {
          fileToUpload = file;
          if ('fileName' in payload && typeof payload.fileName === 'string') {
            payload.fileName = file.name;
          }
        });
        modalRef.result
          .then((res: any) => {
            finalPayload = { ...res, ...payload };
            var formData = new FormData();
            formData.append('file', fileToUpload);
            formData.append('fileFormData', JSON.stringify(finalPayload));
            this.files
              .add(formData, 'File Added')
              .pipe(take(1))
              .subscribe((_res) => this.discardOrSave());
          })
          .catch((_err) => false);
        break;
      default:
        return;
    }
  }

  discardOrSave(res?: any): void {
    if (this.form.pristine && !this.form.dirty) {
      this.setContent();
    } else {
      const confirmDiscardOrSaveModal = this.modal.open(ConfirmCancelComponent);
      confirmDiscardOrSaveModal.componentInstance.data = {
        title: 'Unsaved Changes',
        body: 'You have unsaved changes, do you want to save or discard unsaved changes?',
        cancelBtnText: 'Discard',
        confirmBtnText: 'Save Changes',
      };
      confirmDiscardOrSaveModal.result
        .then((_res) => this.onSubmit())
        .catch((_err) => this.setContent());
    }
  }

  updateProfileOrder(event: any, index: number): void {
    const payload = {
      profileBioOrder: this.pageContent!.sections?.[index]!
        .profileBios as IProfileBio[],
    };
    this.profileBio
      .updateOrder(payload, 'Profile Order Updated')
      .pipe(take(1))
      .subscribe((_res) => false);
  }

  updateSponsorOrder(event: any, index: number): void {
    const payload = {
      sponsorOrder: this.pageContent!.sections?.[index]!.sponsors as ISponsor[],
    };
    this.sponsor
      .updateOrder(payload, 'Sponsor Order Updated')
      .pipe(take(1))
      .subscribe((_res) => false);
  }

  updateFilesOrder(event: any, index: number): void {
    const payload = {
      pdfFileOrder: this.pageContent!.sections?.[index]!.pdfFiles as IPdfFile[],
    };
    this.files
      .updateOrder(payload, 'Files Order Updated')
      .pipe(take(1))
      .subscribe((_res) => false);
  }

  handleProfileDelete(event?: IProfileBio): void {
    const modalData: IModalData = {
      title: 'Confirm Delete',
      body: `Are you sure you want to delete <b>${event?.name}</b>?`,
      cancelBtnText: 'Cancel',
      confirmBtnText: 'Delete',
    };
    const modalRef = this.modal.open(ConfirmCancelComponent, {
      centered: true,
    });
    modalRef.componentInstance.data = modalData;
    modalRef.result
      .then((_res: any) => {
        this.profileBio
          .delete(event!.id, 'Profile deleted.')
          .pipe(take(1))
          .subscribe((_res) => {
            this.discardOrSave();
          });
      })
      .catch((_err: any) => false);
  }

  handleSponsorDelete(event?: ISponsor): void {
    const modalData: IModalData = {
      title: 'Confirm Delete',
      body: `Are you sure you want to delete <b>${event?.name}</b>?`,
      cancelBtnText: 'Cancel',
      confirmBtnText: 'Delete',
    };
    const modalRef = this.modal.open(ConfirmCancelComponent, {
      centered: true,
    });
    modalRef.componentInstance.data = modalData;
    modalRef.result
      .then((_res: any) => {
        this.sponsor
          .delete(event!.id, 'Sponsor deleted.')
          .pipe(take(1))
          .subscribe((_res) => {
            this.discardOrSave();
          });
      })
      .catch((_err: any) => false);
  }

  handleFileDelete(event?: IPdfFile): void {
    const modalData: IModalData = {
      title: 'Confirm Delete',
      body: `Are you sure you want to delete <b>${event?.fileDisplayName}</b>?`,
      cancelBtnText: 'Cancel',
      confirmBtnText: 'Delete',
    };
    const modalRef = this.modal.open(ConfirmCancelComponent, {
      centered: true,
    });
    modalRef.componentInstance.data = modalData;
    modalRef.result
      .then((_res: any) => {
        this.files
          .delete(event!.id, 'File deleted.')
          .pipe(take(1))
          .subscribe((_res) => {
            this.discardOrSave();
          });
      })
      .catch((_err: any) => false);
  }

  onSubmit(): void {
    if (this.pageType && this.pageType.value === PageType.Internal) {
      const payload: IPageAddRequest = this.handleInternalForm();
      this.handleCommonBlock(payload);
    } else if (this.pageType && this.pageType.value === PageType.External) {
      this.handleExternalForm();
    }
  }

  handleInternalForm(): IPageAddRequest {
    const editorContentControl = this.internalForm.get('editorContent');

    const payload = {
      id: this.pageContent?.id ?? 0,
      title: this.pageTitle?.value,
      body: typeof editorContentControl?.value === 'string'
        ? editorContentControl.value || ''
        : toHTML(editorContentControl?.value),
      menuItemId: this.pageContent?.menuItemId || 0,
      routerLink: this.internalForm.get('routerLink')?.value,
    };    

    return payload;
  }

  handleExternalForm(): void {
    const form = this.externalForm;
    const menuItemUpdate: Partial<IMenuItem> = {
      hrefLink: form.get('hrefLink')?.value,
      name: form.get('pageTitle')?.value,
      isPublished: true,
    };

    this.link.add(menuItemUpdate, 'External Link Saved').subscribe(() => {
      this.setContent();
      form.markAsPristine();
      form.markAsUntouched();
      this.router.navigateByUrl('/admin/menu-editor');
    });
  }

  handleCommonBlock(payload: IPageAddRequest): void {
    const formData = new FormData();
    formData.append('file', this.imageFile);
    formData.append('page', JSON.stringify(payload));

    this.page
      .save(formData, 'Page Saved')
      .pipe(take(1))
      .subscribe((_res: any) => {
        this.setContent();
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.router.navigateByUrl('/admin/menu-editor');
      });
  }

  confirmDiscard(): void {
    if (!this.form.dirty && this.form.pristine) {
      this.router.navigateByUrl('/admin/menu-editor');
      return;
    }
    const modalData: IModalData = {
      title: 'Confirm Discard Changes',
      body: 'Are you sure you want to discard your changes?',
      cancelBtnText: 'Continue Editing',
      confirmBtnText: 'Discard Unsaved Changes',
    };
    const modalRef = this.modal.open(ConfirmCancelComponent, {
      centered: true,
    });
    modalRef.componentInstance.data = modalData;
    modalRef.result
      .then((_res: any) => {
        this.router.navigateByUrl('/admin/menu-editor');
      })
      .catch((_err: any) => false);
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      this.imageFile = file;
      this.internalForm.get('fileName')?.setValue(file.name);
      return;
    }
  }

  drop(event: CdkDragDrop<string[]>, index: number, type: string): void {
    if (type == 'profileBios') {
      moveItemInArray(
        this.pageContent!.sections?.[index]!.profileBios as IProfileBio[],
        event.previousIndex,
        event.currentIndex
      );
    } else if (type == 'sponsor') {
      moveItemInArray(
        this.pageContent!.sections?.[index]!.sponsors as ISponsor[],
        event.previousIndex,
        event.currentIndex
      );
    } else if (type == 'files') {
      moveItemInArray(
        this.pageContent!.sections?.[index]!.pdfFiles as IPdfFile[],
        event.previousIndex,
        event.currentIndex
      );
    }
  }

  toggleForm(type: string): void {
    this.showInternalContent = type === PageType.Internal;
    this.showExternalContent = type === PageType.External;
    this.pageType.setValue(type);

    // Clear validators for all controls in the form
    Object.keys(this.form.controls).forEach(controlName => {
      const control = this.form.get(controlName);
      if (control) {
        control.clearValidators();
        control.updateValueAndValidity();
      }
    });

    // Initialize or clear the internal and external forms
    if (type === PageType.Internal) {
      this.initializeInternalForm();
    } else if (type === PageType.External) {
      this.initializeExternalForm();
    }

    this.form.updateValueAndValidity();
  }

  initializeInternalForm(): void {
    this.form.setControl('pageTitle', this.internalForm.get('pageTitle'));
    this.form.setControl('routerLink', this.internalForm.get('routerLink'));
    this.form.setControl('fileUpload', this.internalForm.get('fileUpload'));
    this.form.setControl('editorContent', this.internalForm.get('editorContent'));
  }

  initializeExternalForm(): void {
    this.form.setControl('pageTitle', this.externalForm.get('pageTitle'));
    this.form.setControl('hrefLink', this.externalForm.get('hrefLink'));
  }

  ngOnDestroy(): void {
    if (this.editor) {
      this.editor.destroy();
    }
  }
}